import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp, TrendingDown, Coins, Code2, Copy, Trash2, Plus, Zap, ArrowRight, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { loginRedirect } from "@/components/Layout";
import { Link } from "react-router-dom";
import { adjustmentText } from "@/components/live/RatePrice";

const POLL_MS = 30000;
const STALE_MS = 10 * 60 * 1000;

const METAL_COLOR = {
  gold: "#DDA77B",
  silver: "#94A3B8",
  platinum: "#64748B",
  copper: "#B45309",
};

export default function Prices() {
  const { user } = useAuth();
  const [board, setBoard] = useState(null);
  const [plans, setPlans] = useState({});
  const [keys, setKeys] = useState([]);
  const [label, setLabel] = useState("My app");
  const [plan, setPlan] = useState("free");
  const [tab, setTab] = useState("board"); // board | api

  const [failedPolls, setFailedPolls] = useState(0);
  const load = async () => {
    const [b, p] = await Promise.allSettled([
      api.get("/prices/public"),
      api.get("/api-portal/plans"),
    ]);
    if (b.status === "fulfilled") { setBoard(b.value.data); setFailedPolls(0); }
    else setBoard(prev => prev || { status: "UNAVAILABLE", is_live: false });
    if (p.status === "fulfilled") setPlans(p.value.data.plans || {});
    if (user) {
      try {
        const k = await api.get("/api-portal/keys");
        setKeys(k.data);
      } catch {}
    }
  };
  useEffect(() => { load(); }, [user]);

  // Auto-refresh; failures must not leave a stale board labelled LIVE.
  useEffect(() => {
    const iv = setInterval(() => {
      api.get("/prices/public")
        .then(r => { setBoard(r.data); setFailedPolls(0); })
        .catch(() => setFailedPolls(n => n + 1));
    }, POLL_MS);
    return () => clearInterval(iv);
  }, []);
  const lastOk = board?.last_success_at ? new Date(board.last_success_at).getTime() : 0;
  const stale = failedPolls >= 2 || (lastOk && Date.now() - lastOk > STALE_MS);
  const status = board?.status === "LIVE" && stale ? "DELAYED" : board?.status;
  const isLive = status === "LIVE" && !!board?.is_live;

  const create = async () => {
    if (!user) { loginRedirect(); return; }
    if (!label.trim()) { toast.error("Give the key a label"); return; }
    try {
      const r = await api.post("/api-portal/keys", { label, plan });
      toast.success("API key created");
      setKeys(k => [r.data, ...k]);
    } catch (e) {
      toast.error("Could not create key");
    }
  };

  const revoke = async (id) => {
    if (!confirm("Revoke this key?")) return;
    await api.delete(`/api-portal/keys/${id}`);
    setKeys(k => k.filter(x => x.key_id !== id));
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-8">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className={`inline-flex items-center gap-2 rounded-full text-white px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.18em] ${isLive ? "bg-[#2D5A27]" : "bg-stone-600"}`} data-testid="price-board-status">
            <span className={`w-1.5 h-1.5 rounded-full ${isLive ? "bg-[#DDA77B] animate-pulse" : "bg-stone-300"}`} />
            Metal Pocket · {status === "LIVE" ? "Live Price Board" : status === "DELAYED" ? "Price Board · Delayed (last validated quote)" : "Price Board · Unavailable"}
          </div>
          <h1 className="heading text-3xl md:text-5xl font-extrabold text-stone-900 mt-3">{isLive ? "Real-time metal prices." : "Metal prices."}<br /><span className="text-[#E05E36]">Your own market feed.</span></h1>
          <p className="text-stone-600 mt-2 max-w-2xl">Gold 24K/22K/18K estimates (GST excluded) and international spot silver, platinum &amp; copper in INR per gram, from gold-api.com. Built and published by Metal Pocket. Buy an API key to plug this feed into your own site, app or trading terminal.</p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Last successful update</div>
          <div className="text-sm font-bold text-stone-900" data-testid="price-board-last-success">{board?.last_success_at ? new Date(board.last_success_at).toLocaleString() : "—"}</div>
          <div className="text-[10px] text-stone-500 mt-1" data-testid="price-board-source">Source: {board?.source || "—"}{board?.is_fallback ? " (static fallback)" : ""}</div>
          <div className="text-[10px] text-stone-500 mt-1">Checks for new prices every {POLL_MS / 1000}s</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-6 inline-flex bg-stone-100 rounded-full p-1" role="tablist">
        {[
          { key: "board", label: "Price Board" },
          { key: "api", label: "API Portal" },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`text-xs font-bold px-4 py-2 rounded-full transition-colors ${tab === t.key ? "bg-white text-stone-900 shadow-sm" : "text-stone-500"}`}
            data-testid={`prices-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "board" && (
        <>
          {/* Quotes grid */}
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {board && !board.quotes?.length && (
              <div className="sm:col-span-2 lg:col-span-4 rounded-2xl border border-dashed border-stone-300 p-6 text-sm text-stone-600" data-testid="price-board-unavailable">
                Prices unavailable right now — no validated market quote. We never show estimated or placeholder numbers; this board refreshes automatically.
              </div>
            )}
            {board?.quotes?.filter(q => q.price != null).map(q => {
              const color = METAL_COLOR[q.metal];
              const up = q.day_change_pct >= 0;
              return (
                <motion.div key={`${q.metal}-${q.purity || ""}`} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-stone-200 rounded-2xl p-5" data-testid={`quote-${q.metal}${q.purity ? `-${q.purity}` : ""}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-9 h-9 rounded-xl grid place-items-center text-white" style={{ background: color }}><Coins size={16} /></div>
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500">{q.symbol}</div>
                        <div className="heading font-extrabold text-stone-900 capitalize">{q.metal}{q.purity ? ` ${q.purity}` : ""}</div>
                      </div>
                    </div>
                    {q.day_change_pct != null && (
                      <div className={`flex items-center gap-1 text-xs font-bold ${up ? "text-[#2D5A27]" : "text-red-600"}`}>
                        {up ? <TrendingUp size={13} /> : <TrendingDown size={13} />} {up ? "+" : ""}{q.day_change_pct}%
                      </div>
                    )}
                  </div>
                  <div className="mt-4 heading text-3xl font-extrabold" style={{ color }} data-testid={`quote-price-${q.metal}-${q.purity || "spot"}`}>₹{q.price.toLocaleString("en-IN")}</div>
                  <div className="text-[10px] text-stone-500 uppercase tracking-widest font-bold">per {q.unit}{q.price_per_10g != null ? ` · ₹${q.price_per_10g.toLocaleString("en-IN")} / 10 g` : ""}</div>
                  <div className="mt-2 text-[10px] text-stone-500">{q.basis}</div>

                </motion.div>
              );
            })}
          </div>

          {board?.international_spot?.xau_usd_per_oz != null && (
            <div className="mt-6 rounded-2xl border border-stone-200 bg-white p-4 text-xs text-stone-700 space-y-1" data-testid="price-calc-breakdown">
              <div className="font-extrabold text-stone-900">How the gold estimate is calculated</div>
              <div>International spot ${board.international_spot.xau_usd_per_oz}/oz × USD/INR {board.international_spot.usd_inr} ÷ 31.1034768 g = ₹{board.international_spot.inr_per_gram}/g</div>
              <div>{adjustmentText(board.calculation)}, configured assumptions = 24K estimate · 22K = 24K × 0.916 · 18K = 24K × 0.750</div>
              <div>Source: {board.source} · source time {board.source_ts ? new Date(board.source_ts).toLocaleString() : "—"} · {stale ? "DELAYED — showing the last validated quote" : board.freshness}</div>
              <div className="font-bold" data-testid="price-notice">{board.notice}</div>
            </div>
          )}

          {/* CTA to invest */}
          <div className="mt-8 rounded-3xl bg-gradient-to-br from-[#2D5A27] to-[#24491f] text-white p-6 md:p-8 flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-white/70">Start today</div>
              <h3 className="heading text-2xl md:text-3xl font-extrabold mt-1">{isLive ? "Buy real metal at these live rates from ₹50" : "Buy real metal from ₹50"}</h3>
              <p className="text-white/80 text-sm mt-1 max-w-xl">Metal Pocket gullak SIP with FIFO tracking · Hold 1+ year for +2% annual interest on face value.</p>
            </div>
            <Link to="/metal-pocket" className="inline-flex items-center gap-2 bg-white text-[#2D5A27] font-bold px-5 py-3 rounded-full hover:scale-105 transition-transform" data-testid="board-to-metal">Open Metal Pocket <ArrowRight size={16} /></Link>
          </div>
        </>
      )}

      {tab === "api" && (
        <>
          <div className="mt-6 grid lg:grid-cols-12 gap-6">
            {/* Plans */}
            <div className="lg:col-span-8 space-y-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">Sell our feed</div>
                <h2 className="heading text-2xl md:text-3xl font-extrabold text-stone-900 mt-1">Plans &amp; pricing</h2>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                {Object.entries(plans).map(([id, p]) => (
                  <div key={id} className={`bg-white border rounded-2xl p-5 ${id === "pro" ? "border-[#E05E36] shadow-lg" : "border-stone-200"}`} data-testid={`plan-${id}`}>
                    {id === "pro" && <div className="text-[9px] uppercase tracking-widest font-extrabold text-white bg-[#E05E36] rounded-full px-2 py-0.5 inline-block mb-2">Popular</div>}
                    <div className="heading font-bold text-stone-900 capitalize">{id}</div>
                    <div className="heading text-3xl font-extrabold text-stone-900 mt-2">
                      {p.price_inr > 0 ? <>₹{p.price_inr.toLocaleString("en-IN")}<span className="text-sm font-semibold text-stone-500">/mo</span></> : "Free"}
                    </div>
                    <div className="text-xs text-stone-500 mt-1">{p.daily_limit.toLocaleString("en-IN")} calls / day</div>
                    <ul className="mt-4 space-y-1.5">
                      {p.features.map(f => (
                        <li key={f} className="flex items-center gap-2 text-xs text-stone-700"><Check size={12} className="text-[#2D5A27]" /> {f}</li>
                      ))}
                    </ul>
                    <Button
                      onClick={() => { setPlan(id); document.querySelector('[data-testid=create-key-form]')?.scrollIntoView({ behavior: 'smooth' }); }}
                      className={`w-full mt-4 rounded-full ${id === "pro" ? "bg-[#E05E36] hover:bg-[#c94e2a]" : "bg-stone-900 hover:bg-stone-800"}`}
                      data-testid={`select-plan-${id}`}
                    >
                      Choose {id}
                    </Button>
                  </div>
                ))}
              </div>

              {/* Sample */}
              <div className="bg-stone-900 text-stone-100 rounded-2xl p-5 font-mono text-xs overflow-x-auto">
                <div className="text-stone-400 text-[10px] uppercase tracking-widest mb-2">Sample response · GET /api/prices/public</div>
                <pre className="whitespace-pre">{JSON.stringify(board || {}, null, 2).slice(0, 800)}</pre>
              </div>
            </div>

            {/* Key manager */}
            <div className="lg:col-span-4 space-y-4">
              <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="create-key-form">
                <div className="flex items-center gap-2 mb-3">
                  <Code2 size={18} className="text-[#E05E36]" />
                  <div className="heading font-bold text-stone-900">Generate API key</div>
                </div>
                {!user ? (
                  <>
                    <p className="text-xs text-stone-500 mb-3">Login to generate a key.</p>
                    <Button onClick={loginRedirect} className="w-full rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="api-login-btn">Login with Google</Button>
                  </>
                ) : (
                  <>
                    <Input placeholder="Key label (e.g., My app)" value={label} onChange={e => setLabel(e.target.value)} className="rounded-xl mb-2" data-testid="key-label" />
                    <Select value={plan} onValueChange={setPlan}>
                      <SelectTrigger className="rounded-xl mb-2" data-testid="key-plan"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(plans).map(([id, p]) => <SelectItem key={id} value={id}>{id} · {p.daily_limit}/day</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button onClick={create} className="w-full rounded-full bg-[#2D5A27] hover:bg-[#24491f] gap-2" data-testid="create-key-btn">
                      <Plus size={14} /> Create key
                    </Button>
                    {plan !== "free" && plans[plan] && (
                      <div className="mt-2 text-[11px] text-stone-500">Paid plans require checkout · gateway integration pending (demo)</div>
                    )}
                  </>
                )}
              </div>

              {keys.length > 0 && (
                <div className="bg-white border border-stone-200 rounded-2xl p-5">
                  <div className="heading font-bold text-stone-900 mb-3">Your keys</div>
                  <div className="space-y-2">
                    {keys.map(k => (
                      <div key={k.key_id} className="flex items-center gap-2 p-2 rounded-xl bg-stone-50" data-testid={`key-${k.key_id}`}>
                        <Zap size={14} className="text-[#E05E36] shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-bold text-stone-900 truncate">{k.label} · <span className="uppercase text-[10px] text-stone-500">{k.plan}</span></div>
                          <div className="font-mono text-[10px] text-stone-500 truncate">{k.key}</div>
                        </div>
                        <button onClick={() => copy(k.key)} className="text-stone-500 hover:text-stone-800 p-1" title="Copy" data-testid={`copy-key-${k.key_id}`}><Copy size={13} /></button>
                        <button onClick={() => revoke(k.key_id)} className="text-stone-400 hover:text-red-600 p-1" title="Revoke" data-testid={`revoke-key-${k.key_id}`}><Trash2 size={13} /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
